import React, { useState, useRef, useEffect, useContext } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  Box,
  Card,
  CardContent,
  Avatar,
  Typography,
  Tab,
  Tabs,
  Button,
  Paper,
  IconButton,
  Tooltip,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Chip,
  Stack,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  Divider,
  CircularProgress,
  AlertTitle,
  useMediaQuery,
  useTheme,
  Menu as MuiMenu
} from '@mui/material';
import {
  Camera,
  Share2,
  Download,
  HelpCircle,
  Edit3,
  Linkedin,
  Globe,
  MapPin,
  Phone,
  Mail,
  Building,
  Save,
  Upload,
  Copy,
  Image,
  Code,
  Youtube,
  Palette,
  Layout,
  FileText,
  FileOutput,
  Monitor,
  Menu as MenuIcon,
  Settings, RefreshCcw
} from 'lucide-react';
// Import the DB service
import { 
  saveSettings, 
  loadSettings, 
  clearSettings, 
  saveToCookie, 
  loadFromCookie, 
  clearCookie, 
  isIndexedDBAvailable,
  SETTINGS_KEYS
} from './db-service';
import html2canvas from 'html2canvas';
import axios from 'axios';  // Make sure to install axios with: npm install axios
import { AuthContext } from '../../contexts/auth/AuthContext'; // Adjust the path as needed
import adminService from './bulkopsadmin/AdminService';
import { useNavigate, Link } from 'react-router-dom';
import HtmlPreviewTab from './ProfilePageHtmlPreviewTab';
import EnhancedQRCodeGenerator from './ProfilePageQRCodeGenerator';
import EmailSignatureGenerator from './ProfilePageEmailSignatureGenerator';
import SignatureTemplateGallery from './ProfilePageSignatureTemplateGallery';
import AiSignatureContent from './ProfilePageAiSignatureContent';
import ProfilePageDigitalWalletQR from './ProfilePageDigitalWalletQR';
import { useFeatures } from '../../contexts/auth/FeatureContext';
import ProfilePageIDCard from './ProfilePageIDCard';

const a11yProps = (index) => ({
  id: `profile-tab-${index}`,
  'aria-controls': `profile-tabpanel-${index}`,
});
const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
      sx={{
        display: value !== index ? 'none' : 'block',
        p: 3
      }}
    >
      {value === index && children}
    </Box>
  );
};


// Main Profile Page Component
const ProfilePage = ({ adminMode = false, userData = null, userId = null, onUpdateSuccess = null }) => {
  // API URL
  const API_URL = process.env.REACT_APP_API_URL;
  const [useUrlSettingsStorage, setUseUrlSettingsStorage] = useState(true);
  const urlSettingsInitialLoadComplete = useRef(false);
  const [urlSettingsLoaded, setUrlSettingsLoaded] = useState(false);
  // Get the current user context
  const { currentUser, isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();
  // States
  const [activeTab, setActiveTab] = useState(0);
  const [qrType, setQrType] = useState('profile');
  const [editMode, setEditMode] = useState(false);
  const [skillDialogOpen, setSkillDialogOpen] = useState(false);
  const [languageDialogOpen, setLanguageDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [imagePreview, setImagePreview] = useState(null);
  const [newSkill, setNewSkill] = useState('');
  const [newLanguage, setNewLanguage] = useState('');
  const [loading, setLoading] = useState(false);
  const signatureRef = useRef(null);
  // Add this near the top of your ProfilePage component
  const { isFeatureEnabled } = useFeatures();
  // Admin profile management states
  const [isAdmin, setIsAdmin] = useState(false);
  const [allProfiles, setAllProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState(null);

  // Export Profile States
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('html');
  const [exportFilename, setExportFilename] = useState('');

  // Signature States
  const [signatureTheme, setSignatureTheme] = useState('modern'); // modern, classic, minimal
  const [signatureColor, setSignatureColor] = useState('#2563eb'); // primary color
  const [signatureLayout, setSignatureLayout] = useState('horizontal'); // horizontal, vertical
  const [includePhoto, setIncludePhoto] = useState(true);
  const [includeQR, setIncludeQR] = useState(true);
  const [includeYouTube, setIncludeYouTube] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState('https://youtube.com/@yourprofile');
  const [companySettings, setCompanySettings] = useState({
    enableAiSignature: false,
    enableSignatureTemplates: false
  });
  const isTrial = currentUser?.is_trial || false;
  // Profile Data
  const [profileData, setProfileData] = useState({
    name: "Jane Doe",
    title: "Senior Software Engineer",
    company: "Tech Corp",
    companyUrl: "https://techcorp.com",
    linkedinUrl: "https://linkedin.com/in/janedoe",
    email: "jane.doe@example.com",
    phone: "+1 (555) 123-4567",
    location: "San Francisco, CA",
    companyAddress: "123 Tech Street, San Francisco, CA 94105",
    bio: "Passionate about building intuitive user experiences and solving complex problems through clean, efficient code.",
    avatar: "/api/placeholder/150/150",
    skills: ["React", "Node.js", "Cloud Architecture", "UI/UX", "Team Leadership"],
    languages: ["English", "Spanish"],
    availability: true,
    profileVisibility: "public",
    // For custom URL generation
    profileBaseUrl: "company.com",
    profileUrlType: "name",
    customProfileSlug: "",
    id: null  // Will be populated when loaded from backend
  });
  const fetchCompanySettings = async (companyId) => {
    try {
      // First check if we're in admin mode
      if (adminMode) {
        // If in admin mode and viewing a user from a company, use the userData.companyId
        setCompanySettings({
          enableAiSignature: userData?.company?.enableAiSignature || false,
          enableSignatureTemplates: userData?.company?.enableSignatureTemplates || false
        });
        return;
      }

      // Otherwise fetch settings from the API
      const response = await axios.get(
        `${API_URL}/api/companies/settings`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data) {
        setCompanySettings({
          enableAiSignature: response.data.enableAiSignature || false,
          enableSignatureTemplates: response.data.enableSignatureTemplates || false
        });
      }
    } catch (err) {
      console.error('Error fetching company settings:', err);

      // Default to enabled in development mode
      if (process.env.NODE_ENV === 'development') {
        setCompanySettings({
          enableAiSignature: true,
          enableSignatureTemplates: true
        });
      }
    }
  };
  // Loading overlay component
  const LoadingOverlay = () => (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: 'rgba(255, 255, 255, 0.7)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
      }}
    >
      <CircularProgress size={60} thickness={4} />
      <Typography variant="h6" sx={{ mt: 2 }}>
        Loading Profile...
      </Typography>
    </Box>
  );
  const [templateGalleryOpen, setTemplateGalleryOpen] = useState(false);
  // Check if the current user is an admin
  useEffect(() => {
    if (currentUser && currentUser.role === 'admin') {
      setIsAdmin(true);
      // If user is admin and not in admin mode, fetch all profiles
      if (!adminMode) {
        fetchAllProfiles();
      }
    }
  }, [currentUser, adminMode]);

  // Load profile data from URL parameter (if available and not in admin mode)
  useEffect(() => {
    if (!adminMode) {
      const loadProfileFromUrl = async () => {
        const urlParams = new URLSearchParams(window.location.search);
        const profileId = urlParams.get('id');

        if (profileId) {
          await loadProfile(profileId);
        }
      };

      loadProfileFromUrl();
    }
  }, [adminMode]);

  // Handle admin mode vs. regular user profile loading
  useEffect(() => {
    // If in admin mode and userData is provided, use it
    if (adminMode && userData) {
      if (userData.company) {
        setCompanySettings({
          enableAiSignature: false, //userData.company.enableAiSignature || false,
          enableSignatureTemplates: false, //userData.company.enableSignatureTemplates || false
        });
      }
      // Map the user data properties to profile data properties
      const mappedProfileData = {
        name: userData.name || "",
        title: userData.title || "",
        company: userData.company || "",
        companyUrl: userData.company_url || "",
        linkedinUrl: userData.linkedin_url || "",
        email: userData.email || "",
        phone: userData.phone || "",
        location: userData.location || "",
        companyAddress: userData.company_address || "",
        bio: userData.bio || "",
        avatar: userData.avatar_url
          ? (userData.avatar_url.startsWith("http") || userData.avatar_url.startsWith("data:") ? userData.avatar_url : `${API_URL}${userData.avatar_url}`)
          : userData.avatar || "/api/placeholder/150/150",
        skills: userData.skills || [],
        languages: userData.languages || [],
        availability: userData.availability !== undefined ? userData.availability : true,
        profileVisibility: userData.profile_visibility || userData.profileVisibility || "public",
        profileBaseUrl: userData.profile_base_url || userData.profileBaseUrl || "company.com",
        profileUrlType: userData.profile_url_type || userData.profileUrlType || "name",
        customProfileSlug: userData.custom_profile_slug || userData.customProfileSlug || "",
        id: userData.id,
        userId: userId || userData.user_id || userData.userId
      };

      setProfileData(mappedProfileData);
      // Set export filename
      setExportFilename(mappedProfileData.customProfileSlug || `profile-${mappedProfileData.id}`);
      setLoading(false);
    } else {
      // Load profile and company settings
      fetchUserProfile();

      // If the current user is authenticated, fetch company settings
      if (isAuthenticated && currentUser) {
        fetchCompanySettings();
      }
    }
  }, [adminMode, userData, userId, isAuthenticated, currentUser]);
  useEffect(() => {
    const checkProfileAccess = async () => {
      // Skip checks for admins or if not authenticated
      if (adminMode || !currentUser) {
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const profileId = urlParams.get('id');

      // If there's a profile ID, check if it belongs to the current user
      if (profileId && currentUser.profileId && profileId !== currentUser.profileId.toString() && currentUser.role !== 'admin') {
        setSnackbar({
          open: true,
          message: 'Unauthorized access: You can only access your own profile',
          severity: 'error'
        });

        // Redirect to their own profile
        navigate(`/profiles/profile?id=${currentUser.profileId}`, { replace: true });
      }
    };

    checkProfileAccess();
  }, [adminMode, currentUser, navigate]);
  // Function to fetch user profile based on context
  // Enhanced fetchUserProfile function with access check
  // Replace the existing fetchUserProfile function in ProfilePage.js
  useEffect(() => {
    const loadSavedUrlSettings = async () => {
      try {
        // Skip if in admin mode
        if (adminMode) {
          return;
        }
        
        let savedSettings;
        
        // Try to load from IndexedDB first, fall back to cookies if needed
        if (isIndexedDBAvailable()) {
          savedSettings = await loadSettings(SETTINGS_KEYS.PROFILE_URL);
        } else {
          savedSettings = loadFromCookie(SETTINGS_KEYS.PROFILE_URL);
        }
        
        // Only update state if we found saved settings
        if (savedSettings) {
          // Create a copy of the current profile data to modify
          const updatedProfileData = { ...profileData };
          
          // Update properties if they exist in saved settings
          if (savedSettings.profileBaseUrl) {
            updatedProfileData.profileBaseUrl = savedSettings.profileBaseUrl;
          }
          
          if (savedSettings.profileUrlType) {
            updatedProfileData.profileUrlType = savedSettings.profileUrlType;
          }
          
          if (savedSettings.customProfileSlug) {
            updatedProfileData.customProfileSlug = savedSettings.customProfileSlug;
          }
          
          if (savedSettings.exportFilename) {
            setExportFilename(savedSettings.exportFilename);
          }
          
          // Merge only the URL settings into the latest state: this runs async on mount, so
          // updatedProfileData's other fields are a stale copy that would overwrite the loaded profile
          setProfileData(prev => ({
            ...prev,
            profileBaseUrl: updatedProfileData.profileBaseUrl,
            profileUrlType: updatedProfileData.profileUrlType,
            customProfileSlug: updatedProfileData.customProfileSlug
          }));

          setSnackbar({
            open: true,
            message: 'Your saved profile URL settings have been loaded',
            severity: 'info'
          });
        }

        setUrlSettingsLoaded(true);
      } catch (error) {
        console.error('Error loading saved profile URL settings:', error);
        setUrlSettingsLoaded(true);
      } finally {
        urlSettingsInitialLoadComplete.current = true;
      }
    };
    
    loadSavedUrlSettings();
  }, [adminMode]); // Only run once on mount and when adminMode changes
  
  // Add effect to save URL settings when they change
  useEffect(() => {
    // Skip the first render to avoid saving default settings
    if (!urlSettingsInitialLoadComplete.current || !useUrlSettingsStorage || adminMode) return;
    
    const saveUrlSettings = async () => {
      // Collect all URL settings in one object
      const settingsToSave = {
        profileBaseUrl: profileData.profileBaseUrl,
        profileUrlType: profileData.profileUrlType,
        customProfileSlug: profileData.customProfileSlug,
        exportFilename: exportFilename,
        lastSaved: new Date().toISOString()
      };
      
      // Try IndexedDB first, fall back to cookies
      if (isIndexedDBAvailable()) {
        await saveSettings(settingsToSave, SETTINGS_KEYS.PROFILE_URL);
      } else {
        saveToCookie(settingsToSave, SETTINGS_KEYS.PROFILE_URL);
      }
    };
    
    // Use a debounce to avoid saving on every keystroke
    const timeoutId = setTimeout(() => {
      saveUrlSettings();
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [
    profileData.profileBaseUrl,
    profileData.profileUrlType,
    profileData.customProfileSlug,
    exportFilename,
    useUrlSettingsStorage,
    adminMode
  ]);
  
  // Add these new handler functions
  
  // Toggle URL settings storage on/off
  const handleToggleUrlSettingsStorage = (event) => {
    setUseUrlSettingsStorage(event.target.checked);
    
    if (!event.target.checked) {
      setSnackbar({
        open: true,
        message: 'URL settings storage disabled. Your preferences will not be saved.',
        severity: 'warning'
      });
    } else {
      setSnackbar({
        open: true,
        message: 'URL settings storage enabled. Your preferences will be saved automatically.',
        severity: 'success'
      });
    }
  };
  
  // Reset URL settings to defaults
  const handleResetUrlSettings = async () => {
    // Default settings
    const defaultSettings = {
      profileBaseUrl: "company.com",
      profileUrlType: "name",
      customProfileSlug: "",
      exportFilename: ""
    };
    
    // Update state with defaults
    setProfileData(prev => ({
      ...prev,
      profileBaseUrl: defaultSettings.profileBaseUrl,
      profileUrlType: defaultSettings.profileUrlType,
      customProfileSlug: defaultSettings.customProfileSlug
    }));
    
    setExportFilename(defaultSettings.exportFilename);
    
    // Clear settings from storage
    if (isIndexedDBAvailable()) {
      await clearSettings(SETTINGS_KEYS.PROFILE_URL);
    } else {
      clearCookie(SETTINGS_KEYS.PROFILE_URL);
    }
    
    setSnackbar({
      open: true,
      message: 'Profile URL settings have been reset to defaults',
      severity: 'success'
    });
  };
  
  // Save URL settings explicitly
  const handleSaveUrlSettings = async () => {
    if (!useUrlSettingsStorage) {
      setSnackbar({
        open: true,
        message: 'Please enable settings storage first',
        severity: 'warning'
      });
      return;
    }

    try {
      // Collect all settings in one object
      const settingsToSave = {
        profileBaseUrl: profileData.profileBaseUrl,
        profileUrlType: profileData.profileUrlType,
        customProfileSlug: profileData.customProfileSlug,
        exportFilename: exportFilename,
        lastSaved: new Date().toISOString()
      };
      
      // Try IndexedDB first, fall back to cookies
      if (isIndexedDBAvailable()) {
        await saveSettings(settingsToSave, SETTINGS_KEYS.PROFILE_URL);
      } else {
        saveToCookie(settingsToSave, SETTINGS_KEYS.PROFILE_URL);
      }
      
      setSnackbar({
        open: true,
        message: 'Profile URL settings saved successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving URL settings:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save URL settings',
        severity: 'error'
      });
    }
  };
  // Function to fetch user profile based on context
  const fetchUserProfile = async () => {
    // Only proceed if user is authenticated or in admin mode
    if ((!isAuthenticated && !currentUser) && !adminMode) return;

    try {
      setLoading(true);

      // Check if URL already has a profile ID
      const urlParams = new URLSearchParams(window.location.search);
      const profileIdFromUrl = urlParams.get('id');

      if (profileIdFromUrl) {
        // Security check - only allow if admin or own profile (matching PostgreSQL version)
        if (!adminMode && currentUser) {
          // Quick direct check first (avoid API call if possible)
          if (currentUser.profileId && profileIdFromUrl !== currentUser.profileId.toString() && currentUser.role !== 'admin') {
            console.log('Quick security check - unauthorized profile access attempt', {
              requestedProfileId: profileIdFromUrl,
              userProfileId: currentUser.profileId,
              userRole: currentUser.role
            });

            setSnackbar({
              open: true,
              message: 'You can only access your own profile',
              severity: 'error'
            });

            // If we know their profile ID, redirect there
            if (currentUser.profileId) {
              navigate(`/profiles/profile?id=${currentUser.profileId}`, { replace: true });
              setLoading(false);
              return;
            } else {
              navigate('/profiles/profile', { replace: true });
              setLoading(false);
              return;
            }
          }

          // Additional server-side check - try API call to verify access
          try {
            const accessCheckResponse = await axios.get(
              `${API_URL}/api/profiles/check-access/${profileIdFromUrl}`,
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`
                }
              }
            );

            // If server says no access, redirect
            if (!accessCheckResponse.data.canAccess) {
              console.log('Server security check - unauthorized profile access attempt', accessCheckResponse.data);

              setSnackbar({
                open: true,
                message: accessCheckResponse.data.message || 'Access denied',
                severity: 'error'
              });

              // If we know their profile ID, redirect there
              if (currentUser.profileId) {
                navigate(`/profiles/profile?id=${currentUser.profileId}`, { replace: true });
                setLoading(false);
                return;
              } else {
                navigate('/profiles/profile', { replace: true });
                setLoading(false);
                return;
              }
            }
          } catch (accessError) {
            console.error('Error checking profile access permissions:', accessError);
            // Fail closed - if we can't verify, don't allow access
            setSnackbar({
              open: true,
              message: 'Access verification failed',
              severity: 'error'
            });
            navigate('/profiles/profile', { replace: true });
            setLoading(false);
            return;
          }
        }

        // If security checks pass, load the profile
        await loadProfile(profileIdFromUrl);
        return;
      }

      // If no profile ID in URL, continue with normal profile loading process
      //console.log("Current user in profile loader:", currentUser); // Debug log

      // CORRUPTION FIX: Check if stored profileId matches user email
      if (currentUser && currentUser.profileId) {
        // For office@kooraipro.com, the correct profile ID should be 39, not 68c2ace5cb36586ba73f2897
        if (currentUser.email === 'office@kooraipro.com' && currentUser.profileId === '68c2ace5cb36586ba73f2897') {
          console.warn('🚨 CRITICAL CORRUPTION DETECTED: office@kooraipro.com has wrong profileId, forcing complete reset');
          
          // Complete localStorage cleanup for this specific corruption
          localStorage.clear();
          
          // Force logout and redirect to login
          window.location.href = '/login';
          return;
        }
        
        //console.log("Loading profile from currentUser.profileId:", currentUser.profileId);
        await loadProfile(currentUser.profileId);

        // Update URL with the profile ID
        window.history.pushState(
          {},
          document.title,
          `${window.location.pathname}?id=${currentUser.profileId}`
        );
        return;
      }

      // Rest of your existing fetchUserProfile code...
      // Try to fetch profile by user ID
      if (currentUser) {
        try {
          // console.log("Trying to find profile by user ID:", currentUser.id || currentUser._id);

          // Use the user ID to find the associated profile
          const response = await axios.get(
            `${API_URL}/profiles/${currentUser.id || currentUser._id}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
              }
            }
          );

          // Check if a profile was found
          if (response.data && response.data.id) {
            // console.log("Found profile by user ID:", response.data.id);

            // Load the profile
            await loadProfile(response.data.id);

            // Update URL with the profile ID
            window.history.pushState(
              {},
              document.title,
              `${window.location.pathname}?id=${response.data.id}`
            );
          } else {
            console.log('No existing profile found for user, showing default profile form');
            // Pre-fill the profile form with user data
            setProfileData({
              ...profileData,
              name: currentUser.name || '',
              email: currentUser.email || '',
              userId: currentUser.id || currentUser._id
            });
            setLoading(false);
          }
        } catch (error) {
          console.error('Error fetching profile by user ID:', error);

          // Show a message to the user
          setSnackbar({
            open: true,
            message: 'Could not find your profile. You can create a new one.',
            severity: 'info'
          });

          // Pre-fill the form with available user data
          setProfileData({
            ...profileData,
            name: currentUser.name || '',
            email: currentUser.email || '',
            userId: currentUser.id || currentUser._id
          });
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Error setting up profile:', error);
      setLoading(false);
    }
  };
  // Function to fetch all profiles (for admin only)
  const fetchAllProfiles = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/profile`);
      if (response.data && Array.isArray(response.data)) {
        setAllProfiles(response.data);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching all profiles:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load profiles list',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  // Function to handle profile selection change by admin
  const handleProfileChange = async (event) => {
    const profileId = event.target.value;
    setSelectedProfileId(profileId);

    if (profileId) {
      // Load the selected profile
      await loadProfile(profileId);

      // Update URL with the new profile ID
      window.history.pushState(
        {},
        document.title,
        `${window.location.pathname}?id=${profileId}`
      );
    }
  };

  // Function to load profile data from backend
  const loadProfile = async (profileId) => {
    try {
      setLoading(true);

      //console.log(`Loading profile ID: ${profileId}`);

      // If not in admin mode, verify ownership before loading profile
      if (!adminMode && currentUser) {
        try {
          const accessCheck = await axios.get(
            `${API_URL}/api/profiles/check-access/${profileId}`,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            }
          );

          const { isOwner, canAccess, profileNotFound, redirectToProfile, message } = accessCheck.data;

          // Handle profile not found
          if (profileNotFound) {
            setSnackbar({
              open: true,
              message: message || 'Profile not found. Redirecting to your profile.',
              severity: 'warning'
            });

            const targetProfileId = redirectToProfile || currentUser.profileId || currentUser.id;
            navigate(`/profiles/profile?id=${targetProfileId}`, { replace: true });
            setLoading(false);
            return null;
          }

          // If not owner and not admin, redirect to their own profile
          if (!isOwner && !canAccess && currentUser.role !== 'admin') {
            setSnackbar({
              open: true,
              message: message || 'You do not have permission to view this profile',
              severity: 'error'
            });

            const targetProfileId = redirectToProfile || currentUser.profileId || currentUser.id;
            navigate(`/profiles/profile?id=${targetProfileId}`, { replace: true });
            setLoading(false);
            return null;
          }
        } catch (error) {
          console.error('Error verifying profile ownership:', error);
          setSnackbar({
            open: true,
            message: 'Access denied: Unable to verify profile ownership',
            severity: 'error'
          });

          // Redirect to user's own profile
          const targetProfileId = currentUser.profileId || currentUser.id;
          if (targetProfileId) {
            navigate(`/profiles/profile?id=${targetProfileId}`, { replace: true });
          } else {
            navigate('/profiles/profile', { replace: true });
          }

          setLoading(false);
          return null;
        }
      }

      // Proceed with profile loading if ownership is verified or user is admin
      const response = await axios.get(`${API_URL}/profiles/${profileId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = response.data;

      if (!data || !data.id) {
        throw new Error('Invalid profile data received');
      }

      //console.log("Loaded profile data:", data);

      // Note: Profile access already verified by server-side check above

      // Map database fields to component state with fallbacks
      // This handles different field naming conventions from backend
      setProfileData({
        name: data.name || '',
        title: data.title || '',
        company: data.company || '',
        companyUrl: data.company_url || '',
        linkedinUrl: data.linkedin_url || '',
        email: data.email || '',
        phone: data.phone || '',
        location: data.location || '',
        companyAddress: data.company_address || '',
        bio: data.bio || '',
        avatar: data.avatar_url ?
          (data.avatar_url.startsWith('http') ? data.avatar_url : `${API_URL}${data.avatar_url}`) :
          data.avatar || "/api/placeholder/150/150",
        skills: data.skills || [],
        languages: data.languages || [],
        availability: data.availability !== undefined ? data.availability : true,
        profileVisibility: data.profile_visibility || "public",
        profileBaseUrl: data.profile_base_url || "company.com",
        profileUrlType: data.profile_url_type || "name",
        customProfileSlug: data.custom_profile_slug || data.customProfileSlug || "",
        id: data.id, // Store the profile ID
        userId: data.user_id || data.userId // Also store the user ID if available
      });

      // Update user data in localStorage with profileId for faster access next time
      if (!adminMode) {
        try {
          const userData = JSON.parse(localStorage.getItem('userData') || '{}');
          if (!userData.profileId) {
            userData.profileId = data.id;
            localStorage.setItem('userData', JSON.stringify(userData));
            //console.log("Updated user data in localStorage with profileId:", data.id);
          }
        } catch (e) {
          console.error("Error updating localStorage:", e);
        }
      }

      // Set export filename
      setExportFilename(data.custom_profile_slug || data.customProfileSlug || `profile-${data.id}`);

      setSnackbar({
        open: true,
        message: 'Profile loaded successfully',
        severity: 'success'
      });

      return data.id;
    } catch (error) {
      console.error('Error loading profile:', error);
      setSnackbar({
        open: true,
        message: `Failed to load profile: ${error.message}`,
        severity: 'error'
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Common Card Props
  const cardProps = {
    elevation: 3,
    sx: {
      height: '100%',
      borderRadius: 2,
      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 8,
      },
      '& .MuiCardContent-root': {
        height: '100%',
        p: 3
      }
    }
  };

  // Helper function to generate a consistent profile slug
  const getProfileSlug = () => {
    if (profileData.profileUrlType === "custom" && profileData.customProfileSlug) {
      return profileData.customProfileSlug;
    } else if (profileData.profileUrlType === "id") {
      return `user-${profileData.id}`;
    } else {
      // Default to name-based slug
      return profileData.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    }
  };

  // Event Handlers
  const handleImageUpload = (event) => {
    const file = event.target.files[0];

    if (file) {
      // Check file type
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        setSnackbar({
          open: true,
          message: 'Invalid image format. Only JPEG and PNG are supported.',
          severity: 'error'
        });
        return;
      }

      // Check file size (limit to 2MB)
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (file.size > maxSize) {
        setSnackbar({
          open: true,
          message: 'Image too large. Maximum size is 2MB.',
          severity: 'error'
        });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setProfileData({ ...profileData, avatar: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      // Format data for API with consistent field names
      const apiData = {
        name: profileData.name,
        title: profileData.title,
        company: profileData.company,
        company_url: profileData.companyUrl,
        linkedin_url: profileData.linkedinUrl,
        email: profileData.email,
        phone: profileData.phone,
        location: profileData.location,
        company_address: profileData.companyAddress,
        bio: profileData.bio,
        avatar: profileData.avatar,
        profile_visibility: profileData.profileVisibility,
        profile_base_url: profileData.profileBaseUrl,
        profile_url_type: profileData.profileUrlType,
        custom_profile_slug: profileData.customProfileSlug,
        availability: profileData.availability,
        skills: profileData.skills,
        languages: profileData.languages,
        // Include user ID if available
        user_id: profileData.userId || (currentUser ? (currentUser.id || currentUser._id) : null)
      };

      let profileId = profileData.id;
      let responseData;

      if (adminMode) {
        // Use admin service to update the profile
        // console.log(`Admin updating profile for user ID: ${profileData.userId}`);
        await adminService.updateUserProfile(profileData.userId, apiData);

        // Call the callback if provided
        if (onUpdateSuccess && typeof onUpdateSuccess === 'function') {
          onUpdateSuccess();
        }

        setSnackbar({
          open: true,
          message: 'User profile updated successfully by admin!',
          severity: 'success'
        });
      } else if (profileId) {
        // Update existing profile
        //console.log(`Updating profile ID: ${profileId}`);
        const response = await axios.put(`${API_URL}/profiles/${profileId}`, apiData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        responseData = response.data;

        setSnackbar({
          open: true,
          message: 'Profile updated successfully!',
          severity: 'success'
        });
      } else {
        // Create new profile
        const userId = profileData.userId || (currentUser ? (currentUser.id || currentUser._id) : null);
        //console.log("Creating new profile for user ID:", userId);

        if (!userId && !adminMode) {
          throw new Error('User ID not found. Please log out and log in again.');
        }

        const response = await axios.post(`${API_URL}/profiles`, apiData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        responseData = response.data;
        profileId = responseData.id;

        if (!profileId) {
          throw new Error('Failed to get ID from created profile');
        }

        // SECURITY: Update user profile ID in local storage (safe here as we just created a new profile for this user)
        if (!adminMode) {
          const userData = JSON.parse(localStorage.getItem('userData') || '{}');
          userData.profileId = profileId;
          localStorage.setItem('userData', JSON.stringify(userData));
          console.log("✅ SECURITY: Updated user data with newly created profileId:", profileId);
        }

        // Update URL with new profile ID
        window.history.pushState(
          {},
          document.title,
          `${window.location.pathname}?id=${profileId}`
        );

        setSnackbar({
          open: true,
          message: 'Profile created successfully!',
          severity: 'success'
        });
      }

      // Reload the profile with fresh data from server
      if (!adminMode) {
        await loadProfile(profileId);
      }

      setEditMode(false);
    } catch (error) {
      console.error('Error saving profile:', error);
      setSnackbar({
        open: true,
        message: `Failed to save profile: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddSkill = () => {
    if (newSkill.trim()) {
      setProfileData({
        ...profileData,
        skills: [...profileData.skills, newSkill.trim()]
      });
      setNewSkill('');
      setSkillDialogOpen(false);
    }
  };

  const handleAddLanguage = () => {
    if (newLanguage.trim()) {
      setProfileData({
        ...profileData,
        languages: [...profileData.languages, newLanguage.trim()]
      });
      setNewLanguage('');
      setLanguageDialogOpen(false);
    }
  };

  const handleDeleteProfile = async () => {
    if (!profileData.id) return;

    if (window.confirm('Are you sure you want to delete this profile? This action cannot be undone.')) {
      try {
        setLoading(true);

        if (adminMode) {
          // Use admin service to delete the profile
          await adminService.deleteUser(profileData.userId);

          if (onUpdateSuccess && typeof onUpdateSuccess === 'function') {
            onUpdateSuccess('deleted');
          }
        } else {
          await axios.delete(`${API_URL}/profiles/${profileData.id}`);
        }

        setSnackbar({
          open: true,
          message: 'Profile deleted successfully!',
          severity: 'success'
        });

        // Reset profile data and URL if not in admin mode
        if (!adminMode) {
          setProfileData({
            name: "Jane Doe",
            title: "Senior Software Engineer",
            company: "Tech Corp",
            companyUrl: "https://techcorp.com",
            linkedinUrl: "https://linkedin.com/in/janedoe",
            email: "jane.doe@example.com",
            phone: "+1 (555) 123-4567",
            location: "San Francisco, CA",
            companyAddress: "123 Tech Street, San Francisco, CA 94105",
            bio: "Passionate about building intuitive user experiences and solving complex problems through clean, efficient code.",
            avatar: "/api/placeholder/150/150",
            skills: ["React", "Node.js", "Cloud Architecture", "UI/UX", "Team Leadership"],
            languages: ["English", "Spanish"],
            availability: true,
            profileVisibility: "public",
            profileBaseUrl: "company.com",
            profileUrlType: "name",
            customProfileSlug: "",
            id: null
          });

          window.history.pushState(
            {},
            document.title,
            window.location.pathname
          );

          // Refresh the profiles list if admin
          if (isAdmin) {
            fetchAllProfiles();
          }
        }
      } catch (error) {
        console.error('Error deleting profile:', error);
        setSnackbar({
          open: true,
          message: 'Failed to delete profile',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const getQRData = () => {
    if (qrType === 'profile') {
      return `https://${profileData.profileBaseUrl}/profile/${getProfileSlug()}`;
    }
    return generateVCard();
  };

  const generateVCard = () => {
    return `BEGIN:VCARD
VERSION:3.0
FN:${profileData.name}
TITLE:${profileData.title}
ORG:${profileData.company}
EMAIL:${profileData.email}
TEL:${profileData.phone}
ADR:;;${profileData.companyAddress}
URL:${profileData.companyUrl}
URL;type=LINKEDIN:${profileData.linkedinUrl}
NOTE:${profileData.bio}
END:VCARD`;
  };

  const downloadQR = () => {
    try {
      const svg = document.getElementById('qr-code');
      if (!svg) {
        throw new Error('QR code element not found');
      }

      const svgData = new XMLSerializer().serializeToString(svg);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        const pngUrl = canvas.toDataURL('image/png');
        const downloadLink = document.createElement('a');
        downloadLink.href = pngUrl;
        downloadLink.download = `${profileData.name}-${qrType}-qr.png`
          .toLowerCase()
          .replace(/\s+/g, '-');
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);

        setSnackbar({
          open: true,
          message: 'QR Code downloaded successfully!',
          severity: 'success'
        });
      };

      img.onerror = () => {
        throw new Error('Error loading QR code image');
      };

      img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    } catch (error) {
      console.error('Error downloading QR code:', error);
      setSnackbar({
        open: true,
        message: 'Failed to download QR Code',
        severity: 'error'
      });
    }
  };

  // Handle signature copying
  const copySignatureHTML = () => {
    try {
      if (!signatureRef.current) {
        throw new Error('Signature element not found');
      }

      const signatureHTML = signatureRef.current.innerHTML;
      navigator.clipboard.writeText(signatureHTML);

      setSnackbar({
        open: true,
        message: 'HTML signature copied to clipboard!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error copying signature HTML:', error);
      setSnackbar({
        open: true,
        message: 'Failed to copy signature HTML',
        severity: 'error'
      });
    }
  };

  const downloadSignatureImage = async () => {
    try {
      if (!signatureRef.current) {
        throw new Error('Signature element not found');
      }

      const canvas = await html2canvas(signatureRef.current, {
        scale: 2,
        backgroundColor: null,
        logging: false
      });

      const imageUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = imageUrl;
      downloadLink.download = `${profileData.name.toLowerCase().replace(/\s+/g, '-')}-signature.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      setSnackbar({
        open: true,
        message: 'Signature image downloaded successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error downloading signature image:', error);
      setSnackbar({
        open: true,
        message: 'Failed to download signature image',
        severity: 'error'
      });
    }
  };

  // Export Profile Functions
  const handleExportDialogOpen = () => {
    // Initialize export filename with profile slug if empty
    // if (!exportFilename) {
    //     setExportFilename(getProfileSlug());
    // }
    // setExportDialogOpen(true);
    setActiveTab(3);
  };

  const handleExportDialogClose = () => {
    setExportDialogOpen(false);
  };

  const downloadProfilePage = () => {
    try {
      const format = exportFormat;
      const filename = exportFilename || getProfileSlug();

      // Generate the content based on format
      let content = generateProfilePageContent(format);

      // Create a blob and download
      const blob = new Blob([content], {
        type: format === 'html' ? 'text/html' : 'application/octet-stream'
      });
      const url = URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = `${filename}.${format}`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(url);

      setExportDialogOpen(false);
      setSnackbar({
        open: true,
        message: `Profile page exported as ${format.toUpperCase()} successfully!`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error downloading profile page:', error);
      setSnackbar({
        open: true,
        message: 'Failed to export profile page',
        severity: 'error'
      });
    }
  };

  // Copy profile page content to clipboard
  const copyProfilePageContent = () => {
    try {
      const content = generateProfilePageContent(exportFormat);
      navigator.clipboard.writeText(content);

      setExportDialogOpen(false);
      setSnackbar({
        open: true,
        message: `Profile page content copied to clipboard!`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error copying profile content:', error);
      setSnackbar({
        open: true,
        message: 'Failed to copy profile content',
        severity: 'error'
      });
    }
  };

  // Generate Profile Page HTML/PHP content
  const generateProfilePageContent = (format) => {
    const {
      name, title, company, companyUrl, linkedinUrl, email, phone,
      location, companyAddress, bio, avatar, skills, languages
    } = profileData;

    // Base styles for the profile
    const styles = `
            * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
                font-family: 'Segoe UI', Arial, sans-serif;
            }
            body {
                background-color: #f5f7fa;
                color: #333;
                line-height: 1.6;
            }
            .container {
                max-width: 1000px;
                margin: 40px auto;
                padding: 0 20px;
            }
            .profile-header {
                background: linear-gradient(135deg, #1a365d 0%, #2563eb 100%);
                border-radius: 15px;
                padding: 40px;
                color: white;
                display: flex;
                align-items: center;
                gap: 30px;
                margin-bottom: 30px;
                box-shadow: 0 10px 25px rgba(0,0,0,0.1);
            }
            .avatar {
                width: 150px;
                height: 150px;
                border-radius: 50%;
                border: 5px solid rgba(255,255,255,0.7);
                object-fit: cover;
                box-shadow: 0 5px 15px rgba(0,0,0,0.2);
            }
            .header-info h1 {
                font-size: 2.5rem;
                margin-bottom: 5px;
                font-weight: 600;
            }
            .header-info h2 {
                font-size: 1.3rem;
                font-weight: 400;
                opacity: 0.9;
                margin-bottom: 15px;
            }
            .bio {
                line-height: 1.7;
                font-size: 1.1rem;
                margin-top: 20px;
                max-width: 700px;
            }
            .section {
                background: white;
                border-radius: 12px;
                padding: 30px;
                margin-bottom: 30px;
                box-shadow: 0 5px 15px rgba(0,0,0,0.05);
            }
            .section h3 {
                font-size: 1.5rem;
                margin-bottom: 20px;
                color: #1a365d;
                padding-bottom: 10px;
                border-bottom: 2px solid #e5e7eb;
            }
            .info-item {
                display: flex;
                align-items: center;
                margin-bottom: 15px;
            }
            .info-icon {
                width: 40px;
                height: 40px;
                background-color: #eef2ff;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-right: 15px;
                flex-shrink: 0;
            }
            .info-text {
                font-size: 1.1rem;
            }
            .info-label {
                font-weight: 600;
                display: block;
                margin-bottom: 3px;
                color: #6b7280;
                font-size: 0.9rem;
            }
            .skills-container {
                display: flex;
                flex-wrap: wrap;
                gap: 10px;
                margin-top: 15px;
            }
            .skill-tag {
                background-color: #eef2ff;
                color: #2563eb;
                padding: 8px 16px;
                border-radius: 20px;
                font-size: 0.9rem;
                font-weight: 500;
                display: inline-block;
            }
            .language-tag {
                background-color: #f3f4f6;
                color: #374151;
                padding: 8px 16px;
                border-radius: 20px;
                font-size: 0.9rem;
                font-weight: 500;
                display: inline-block;
                border: 1px solid #e5e7eb;
            }
            .qr-section {
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 30px;
            }
            .qr-code {
                margin: 20px 0;
            }
            .qr-caption {
                font-size: 0.9rem;
                color: #6b7280;
                text-align: center;
                max-width: 300px;
            }
            .footer {
                text-align: center;
                margin-top: 40px;
                padding-top: 20px;
                border-top: 1px solid #e5e7eb;

                color: #6b7280;
                font-size: 0.9rem;
            }
            .social-links {
                display: flex;
                gap: 15px;
                margin-top: 20px;
                justify-content: center;
            }
            .social-link {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 45px;
                height: 45px;
                background-color: #f3f4f6;
                border-radius: 50%;
                color: #2563eb;
                transition: all 0.3s;
            }
            .social-link:hover {
                background-color: #2563eb;
                color: white;
                transform: translateY(-3px);
                box-shadow: 0 5px 10px rgba(37, 99, 235, 0.2);
            }
            a {
                color: #2563eb;
                text-decoration: none;
            }
            a:hover {
                text-decoration: underline;
            }
            @media (max-width: 768px) {
                .profile-header {
                    flex-direction: column;
                    text-align: center;
                    padding: 30px 20px;
                }
                .avatar {
                    width: 120px;
                    height: 120px;
                }
                .section {
                    padding: 20px;
                }
            }
        `;

    // Create inline SVGs for icons
    const icons = {
      mail: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"></rect><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path></svg>',
      phone: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>',
      location: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path><circle cx="12" cy="10" r="3"></circle></svg>',
      building: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="16" height="20" x="4" y="2" rx="2" ry="2"></rect><path d="M9 22v-4h6v4"></path><path d="M8 6h.01"></path><path d="M16 6h.01"></path><path d="M12 6h.01"></path><path d="M12 10h.01"></path><path d="M12 14h.01"></path><path d="M16 10h.01"></path><path d="M16 14h.01"></path><path d="M8 10h.01"></path><path d="M8 14h.01"></path></svg>',
      globe: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"></path><path d="M2 12h20"></path></svg>',
      linkedin: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect width="4" height="12" x="2" y="9"></rect><circle cx="4" cy="4" r="2"></circle></svg>'
    };

    // Building HTML content
    const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${name} - Professional Profile</title>
    <style>${styles}</style>
</head>
<body>
    <div class="container">
        <header class="profile-header">
            <img src="${avatar}" alt="${name}" class="avatar">
            <div class="header-info">
                <h1>${name}</h1>
                <h2>${title}</h2>
                <p>${company}</p>
                <p class="bio">${bio}</p>
            </div>
        </header>
        
        <section class="section">
            <h3>Contact Information</h3>
            <div class="info-item">
                <div class="info-icon">${icons.mail}</div>
                <div class="info-text">
                    <span class="info-label">Email</span>
                    <a href="mailto:${email}">${email}</a>
                </div>
            </div>
            <div class="info-item">
                <div class="info-icon">${icons.linkedin}</div>
                <div class="info-text">
                    <span class="info-label">LinkedIn</span>
                    <a href="${linkedinUrl}" target="_blank">${linkedinUrl}</a>
                </div>
            </div>
            <div class="info-item">
                <div class="info-icon">${icons.phone}</div>
                <div class="info-text">
                    <span class="info-label">Phone</span>
                    <a href="tel:${phone}">${phone}</a>
                </div>
            </div>
            <div class="info-item">
                <div class="info-icon">${icons.location}</div>
                <div class="info-text">
                    <span class="info-label">Location</span>
                    ${location}
                </div>
            </div>
        </section>
        
        <section class="section">
            <h3>Company Information</h3>
            <div class="info-item">
                <div class="info-icon">${icons.building}</div>
                <div class="info-text">
                    <span class="info-label">Company</span>
                    ${company}
                </div>
            </div>
            <div class="info-item">
                <div class="info-icon">${icons.globe}</div>
                <div class="info-text">
                    <span class="info-label">Website</span>
                    <a href="${companyUrl}" target="_blank">${companyUrl}</a>
                </div>
            </div>
            <div class="info-item">
                <div class="info-icon">${icons.location}</div>
                <div class="info-text">
                    <span class="info-label">Address</span>
                    ${companyAddress}
                </div>
            </div>
        </section>
        
        <section class="section">
            <h3>Skills & Languages</h3>
            <div>
                <h4 style="margin-bottom: 10px; color: #4b5563;">Professional Skills</h4>
                <div class="skills-container">
                    ${skills.map(skill => `<span class="skill-tag">${skill}</span>`).join('')}
                </div>
            </div>
            <div style="margin-top: 25px;">
                <h4 style="margin-bottom: 10px; color: #4b5563;">Languages</h4>
                <div class="skills-container">
                    ${languages.map(lang => `<span class="language-tag">${lang}</span>`).join('')}
                </div>
            </div>
        </section>
        
       
        <footer class="footer">
            <div class="social-links">
                <a href="${linkedinUrl}" target="_blank" class="social-link">
                    ${icons.linkedin}
                </a>
                <a href="${companyUrl}" target="_blank" class="social-link">
                    ${icons.globe}
                </a>
            </div>
            <p style="margin-top: 20px;">© ${new Date().getFullYear()} ${name}. All rights reserved.</p>
        </footer>
    </div>
</body>
</html>`;

    // For PHP, wrap the HTML content with PHP tags and add some basic server-side functionality
    if (format === 'php') {
      return `<?php
// Profile data
$name = "${name}";
$title = "${title}";
$company = "${company}";
$email = "${email}";
$phone = "${phone}";
$location = "${location}";
$bio = "${bio}";
$avatar = "${avatar}";
$companyUrl = "${companyUrl}";
$linkedinUrl = "${linkedinUrl}";
$companyAddress = "${companyAddress}";

// Handle page view counter
$viewsFile = 'profile_views.txt';
$views = 1;
if (file_exists($viewsFile)) {
    $views = (int)file_get_contents($viewsFile);
    $views++;
}
file_put_contents($viewsFile, $views);

// Current date/time
$lastUpdated = date('F j, Y');
?>
${htmlContent.replace('</footer>', `
            <p>This page has been viewed <?php echo $views; ?> times.</p>
            <p>Last updated: <?php echo $lastUpdated; ?></p>
        </footer>`)}`;
    }

    return htmlContent;
  };

  // Signature styles based on theme
  const getSignatureStyles = () => {
    const themes = {
      modern: {
        container: {
          fontFamily: "'Segoe UI', Arial, sans-serif",
          borderLeft: `3px solid ${signatureColor}`,
          padding: '0 0 0 16px',
          maxWidth: signatureLayout === 'horizontal' ? '600px' : '300px',
          display: 'flex',
          flexDirection: signatureLayout === 'horizontal' ? 'row' : 'column',
          alignItems: signatureLayout === 'horizontal' ? 'center' : 'flex-start',
          gap: '16px',
          backgroundColor: '#ffffff'
        },
        name: {
          fontSize: '18px',
          fontWeight: 'bold',
          color: `${signatureColor}`,
          margin: '0 0 2px 0'
        },
        title: {
          fontSize: '14px',
          color: '#666666',
          margin: '0 0 8px 0'
        },
        company: {
          fontSize: '14px',
          fontWeight: 'bold',
          margin: '0 0 12px 0'
        },
        contact: {
          fontSize: '13px',
          margin: '3px 0',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        },
        avatarContainer: {
          marginRight: signatureLayout === 'horizontal' ? '16px' : '0',
          marginBottom: signatureLayout === 'horizontal' ? '0' : '16px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px'
        },
        avatar: {
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          border: `2px solid ${signatureColor}`
        },
        qrCode: {
          display: includeQR ? 'block' : 'none',
          width: '70px',
          height: '70px'
        },
        divider: {
          height: signatureLayout === 'horizontal' ? '50px' : '1px',
          width: signatureLayout === 'horizontal' ? '1px' : '100%',
          backgroundColor: '#e0e0e0',
          margin: signatureLayout === 'horizontal' ? '0 16px' : '8px 0'
        },
        iconContainer: {
          display: 'flex',
          gap: '8px',
          margin: '8px 0 0 0'
        },
        icon: {
          width: '24px',
          height: '24px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: signatureColor,
          borderRadius: '50%',
          padding: '4px'
        }
      },
      classic: {
        container: {
          fontFamily: "'Times New Roman', Times, serif",
          maxWidth: signatureLayout === 'horizontal' ? '600px' : '300px',
          display: 'flex',
          flexDirection: signatureLayout === 'horizontal' ? 'row' : 'column',
          alignItems: signatureLayout === 'horizontal' ? 'center' : 'flex-start',
          gap: '16px',
          backgroundColor: '#ffffff'
        },
        name: {
          fontSize: '18px',
          fontWeight: 'bold',
          color: '#000000',
          margin: '0 0 2px 0'
        },
        title: {
          fontSize: '14px',
          fontStyle: 'italic',
          color: '#444444',
          margin: '0 0 8px 0'
        },
        company: {
          fontSize: '14px',
          fontWeight: 'bold',
          margin: '0 0 12px 0'
        },
        contact: {
          fontSize: '13px',
          margin: '3px 0',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        },
        avatarContainer: {
          marginRight: signatureLayout === 'horizontal' ? '16px' : '0',
          marginBottom: signatureLayout === 'horizontal' ? '0' : '16px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px'
        },
        avatar: {
          width: '80px',
          height: '80px',
          borderRadius: '0',
          border: '1px solid #000000'
        },
        qrCode: {
          display: includeQR ? 'block' : 'none',
          width: '70px',
          height: '70px'
        },
        divider: {
          height: signatureLayout === 'horizontal' ? '50px' : '1px',
          width: signatureLayout === 'horizontal' ? '1px' : '100%',
          backgroundColor: '#000000',
          margin: signatureLayout === 'horizontal' ? '0 16px' : '8px 0'
        },
        iconContainer: {
          display: 'flex',
          gap: '8px',
          margin: '8px 0 0 0'
        },
        icon: {
          width: '24px',
          height: '24px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '4px'
        }
      },
      minimal: {
        container: {
          fontFamily: "'Arial', sans-serif",
          maxWidth: signatureLayout === 'horizontal' ? '600px' : '300px',
          display: 'flex',
          flexDirection: signatureLayout === 'horizontal' ? 'row' : 'column',
          alignItems: signatureLayout === 'horizontal' ? 'center' : 'flex-start',
          gap: '16px',
          backgroundColor: '#ffffff'
        },
        name: {
          fontSize: '16px',
          fontWeight: 'bold',
          color: '#000000',
          margin: '0 0 2px 0'
        },
        title: {
          fontSize: '13px',
          color: '#666666',
          margin: '0 0 8px 0'
        },
        company: {
          fontSize: '13px',
          fontWeight: 'bold',
          margin: '0 0 12px 0'
        },
        contact: {
          fontSize: '12px',
          margin: '2px 0',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        },
        avatarContainer: {
          marginRight: signatureLayout === 'horizontal' ? '16px' : '0',
          marginBottom: signatureLayout === 'horizontal' ? '0' : '16px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px'
        },
        avatar: {
          width: '70px',
          height: '70px',
          borderRadius: '4px'
        },
        qrCode: {
          display: includeQR ? 'block' : 'none',
          width: '60px',
          height: '60px'
        },
        divider: {
          height: signatureLayout === 'horizontal' ? '40px' : '1px',
          width: signatureLayout === 'horizontal' ? '1px' : '100%',
          backgroundColor: '#e0e0e0',
          margin: signatureLayout === 'horizontal' ? '0 16px' : '8px 0'
        },
        iconContainer: {
          display: 'flex',
          gap: '8px',
          margin: '8px 0 0 0'
        },
        icon: {
          width: '20px',
          height: '20px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '2px'
        }
      }
    };

    return themes[signatureTheme];
  };

  // Admin Profile Selection Component
  const AdminProfileSelector = () => {
    // Only show this in normal admin mode (not in the AdminProfileEditor)
    if (!isAdmin || allProfiles.length === 0 || adminMode) return null;

    return (
      <Card
        sx={{
          mb: 4,
          p: 2,
          borderRadius: 2,
          bgcolor: '#fffbeb', // Light yellow background
          border: '1px solid #fbbf24' // Amber border
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', color: '#92400e' }}
          >
            Admin Mode:
          </Typography>
          <FormControl variant="outlined" size="small" sx={{ minWidth: 250, flexGrow: 1 }}>
            <Select
              value={selectedProfileId || ''}
              onChange={handleProfileChange}
              displayEmpty
            >
              <MenuItem value="" disabled>Select a user profile</MenuItem>
              {allProfiles.map(profile => (
                <MenuItem key={profile.id} value={profile.id}>
                  {profile.name} - {profile.email || profile.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => {
              // Clear selection and reset form
              setSelectedProfileId(null);
              setProfileData({
                name: "",
                title: "",
                company: "",
                companyUrl: "",
                linkedinUrl: "",
                email: "",
                phone: "",
                location: "",
                companyAddress: "",
                bio: "",
                avatar: "/api/placeholder/150/150",
                skills: [],
                languages: [],
                availability: true,
                profileVisibility: "public",
                profileBaseUrl: "company.com",
                profileUrlType: "name",
                customProfileSlug: "",
                id: null
              });
              // Clear the URL params
              window.history.pushState({}, document.title, window.location.pathname);
            }}
          >
            Create New Profile
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={fetchAllProfiles}
          >
            Refresh List
          </Button>
        </Box>
      </Card>
    );
  };

  // Responsive Tab Headers Component
  const ResponsiveTabs = ({ activeTab, handleTabChange }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    // Menu state for mobile view
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleMenuClick = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = (index) => {
      setAnchorEl(null);
      if (typeof index === 'number') {
        handleTabChange(null, index);
      }
    };

    // Define tab data
    const tabData = [
      { label: 'Profile Info', icon: <Save className="w-4 h-4" /> },
      { label: 'QR Code', icon: <Camera className="w-4 h-4" /> },
      { label: 'Digital Wallet QR', icon: <FileText className="w-4 h-4" /> },
      { label: 'Email Signature', icon: <Monitor className="w-4 h-4" /> },
      { label: 'HTML Preview', icon: <Copy className="w-4 h-4" /> },
      { label: 'ID Card', icon: <Image size={20} />, color: '#f59e0b' },
      { label: 'Settings', icon: <HelpCircle className="w-4 h-4" /> }
    ];

    // Renders tab content (icon and/or label)
    const renderTabContent = (tab, showLabel = true, showIcon = true) => (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {showIcon && <Box component="span" sx={{ mr: showLabel ? 1 : 0 }}>{tab.icon}</Box>}
        {showLabel && tab.label}
      </Box>
    );

    // For mobile view: show hamburger menu with current tab label
    if (isMobile) {
      return (
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 1,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: '#f8fafc'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', pl: 1 }}>
            {tabData[activeTab].icon}
            <Box component="span" sx={{ ml: 1, fontWeight: 500 }}>
              {tabData[activeTab].label}
            </Box>
          </Box>

          <IconButton
            aria-label="more tabs"
            aria-controls="tabs-menu"
            aria-haspopup="true"
            onClick={handleMenuClick}
          >
            <MenuIcon />
          </IconButton>

          <MuiMenu
            id="tabs-menu"
            anchorEl={anchorEl}
            keepMounted
            open={open}
            onClose={() => handleMenuClose()}
          >
            {tabData.map((tab, index) => (
              <MenuItem
                key={index}
                onClick={() => handleMenuClose(index)}
                selected={activeTab === index}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {tab.icon}
                  <Box component="span" sx={{ ml: 1 }}>
                    {tab.label}
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </MuiMenu>
        </Box>
      );
    }

    // For tablet view: show only icons in tabs, but keep all tabs visible
    if (isTablet) {
      return (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            aria-label="profile tabs"
            sx={{
              backgroundColor: '#f8fafc',
              '& .MuiTab-root': { py: 2 }
            }}
          >
            {tabData.map((tab, index) => (
              <Tab
                key={index}
                label={renderTabContent(tab, false, true)}
                aria-label={tab.label}
                title={tab.label} // Add tooltip via title attribute
                {...a11yProps(index)}
              />
            ))}
          </Tabs>
        </Box>
      );
    }

    // Desktop view: show icons and labels for all tabs
    return (
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            backgroundColor: '#f8fafc',
            '& .MuiTab-root': { py: 2 }
          }}
        >
          {tabData.map((tab, index) => (
            <Tab
              key={index}
              label={renderTabContent(tab, true, true)}
              {...a11yProps(index)}
            />
          ))}
        </Tabs>
      </Box>
    );
  };

  return (
    <Box className="max-w-7xl mx-auto p-6">
      {/* Show loading overlay when loading */}
      {loading && <LoadingOverlay />}

      {/* Admin Profile Selector */}
      <AdminProfileSelector />

      {/* Admin Mode Banner - shown only in admin mode */}
      {adminMode && (
        <Box
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: '#fcefef',
            border: '1px solid #f87171',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 500, color: '#b91c1c' }}>
            Admin Editing Mode: Changes will update the user's profile
          </Typography>
        </Box>
      )}

      {/* Header with Profile Summary */}
      <Card
        elevation={3}
        sx={{
          mb: 4,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #1a365d 0%, #2563eb 100%)',
          transition: 'all 0.3s ease-in-out',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            transform: 'scale(1.01)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
            zIndex: 1
          }
        }}
      >
        <CardContent sx={{ position: 'relative', zIndex: 2, py: 4 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item>
              <Box
                className="relative"
                sx={{
                  '& .MuiAvatar-root': {
                    border: '4px solid rgba(255,255,255,0.8)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }
                }}
              >
                <Avatar
                  src={profileData.avatar}
                  alt={profileData.name}
                  crossOrigin="anonymous"
                  sx={{
                    width: 120,
                    height: 120,
                    bgcolor: 'rgba(255,255,255,0.9)',
                    color: '#1a365d'
                  }}
                />
                {/* <Avatar
                                    src={profileData.avatar}
                                    alt={profileData.name}
                                    sx={{
                                        width: 120,
                                        height: 120,
                                        bgcolor: 'rgba(255,255,255,0.9)',
                                        color: '#1a365d'
                                    }}
                                    /> */}
                {editMode && (
                  <IconButton
                    component="label"
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      bgcolor: 'rgba(255,255,255,0.9)',
                      '&:hover': {
                        bgcolor: 'white',
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.2s ease-in-out',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}
                    size="small"
                  >
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleImageUpload}
                    />
                    <Upload className="w-4 h-4 text-blue-600" />
                  </IconButton>
                )}
              </Box>
            </Grid>
            <Grid item xs>
              {editMode ? (
                <Stack spacing={2}>
                  <TextField
                    value={profileData.name}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                    variant="outlined"
                    label="Name"
                    fullWidth
                    disabled={true}
                    InputProps={{
                      readOnly: true, // Add this to make it read-only
                      sx: {
                        bgcolor: 'rgba(255,255,255,0.8)',
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.8)'
                        },
                        '& .Mui-disabled': {
                          color: 'rgba(0, 0, 0, 0.87)', // Keep text dark even when disabled
                          WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)', // For Safari
                        }
                      }
                    }}
                    helperText="Name cannot be changed"
                  />
                  <TextField
                    value={profileData.title}
                    onChange={(e) => setProfileData({ ...profileData, title: e.target.value })}
                    variant="outlined"
                    label="Title"
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'rgba(255,255,255,0.9)',
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: 'white'
                        }
                      }
                    }}
                  />
                </Stack>
              ) : (
                <Box sx={{ color: 'white' }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 600,
                      mb: 1,
                      textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      color: 'white'
                    }}
                  >
                    {profileData.name}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'rgba(255,255,255,0.9)',
                      textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                    }}
                  >
                    {profileData.title}
                  </Typography>
                </Box>
              )}
            </Grid>
            <Grid item>
              <Button
                variant={editMode ? "contained" : "outlined"}
                startIcon={editMode ? <Save /> : <Edit3 />}
                onClick={editMode ? handleSave : () => setEditMode(true)}
                sx={{
                  bgcolor: editMode ? '#10B981' : 'rgba(255,255,255,0.15)',
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.5)',
                  '&:hover': {
                    bgcolor: editMode ? '#059669' : 'rgba(255,255,255,0.25)',
                    borderColor: 'white'
                  },
                  backdropFilter: 'blur(8px)',
                  textTransform: 'none',
                  px: 3,
                  py: 1,
                  borderRadius: 2
                }}
              >
                {editMode ? "Save Changes" : "Edit Profile"}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Main Content with Tabs */}
      <Paper
        elevation={3}
        className="rounded-xl overflow-hidden"
        sx={{
          backgroundColor: 'background.paper',
          '& .MuiTab-root': {
            minHeight: '64px',
            fontSize: '1rem',
          },
          '& .MuiTabs-indicator': {
            height: '3px',
          }
        }}
      >
        {/* Tab Headers */}
        {/* <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, newValue) => setActiveTab(newValue)}
                        variant="fullWidth"
                        sx={{
                            backgroundColor: '#f8fafc',
                            '& .MuiTab-root': { py: 2 }
                        }}
                    >
                        <Tab
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box component="span" sx={{ mr: 1 }}>Profile Info</Box>
                                    <Save className="w-4 h-4" />
                                </Box>
                            }
                            {...a11yProps(0)}
                        />
                        <Tab
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box component="span" sx={{ mr: 1 }}>QR Code</Box>
                                    <Camera className="w-4 h-4" />
                                </Box>
                            }
                            {...a11yProps(1)}
                        />
                        <Tab
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box component="span" sx={{ mr: 1 }}>Digital Wallet QR</Box>
                                    <FileText className="w-4 h-4" />
                                </Box>
                            }
                            {...a11yProps(4)}
                        />
                        <Tab
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box component="span" sx={{ mr: 1 }}>Email Signature</Box>
                                    <Monitor className="w-4 h-4" />
                                </Box>
                            }
                            {...a11yProps(3)}
                        />
                        <Tab
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box component="span" sx={{ mr: 1 }}>HTML Preview</Box>
                                    <Copy className="w-4 h-4" />
                                </Box>
                            }
                            {...a11yProps(2)}
                        />
                        <Tab
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box component="span" sx={{ mr: 1 }}>Settings</Box>
                                    <HelpCircle className="w-4 h-4" />
                                </Box>
                            }
                            {...a11yProps(5)}
                        />

                    </Tabs>

                </Box> */}
        <ResponsiveTabs
          activeTab={activeTab}
          handleTabChange={(e, newValue) => setActiveTab(newValue)}
        />
        {/* Tab Panels */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            {/* Contact Information */}
            <Grid item xs={12} md={6}>
              <Card {...cardProps}>
                <CardContent>
                  <Box className="flex justify-between items-center mb-4">
                    <Typography variant="h6" className="font-semibold">
                      Contact Information
                    </Typography>
                    <Tooltip title="Your professional contact details">
                      <IconButton size="small">
                        <HelpCircle className="w-4 h-4" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Stack spacing={3}>
                    {editMode ? (
                      <>
                        <TextField
                          value={profileData.email}
                          onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                          label="Email"
                          fullWidth
                          InputProps={{
                            readOnly: true,
                            startAdornment: <Mail className="w-4 h-4 mr-2" />,
                            sx: {
                              '& .Mui-disabled': {
                                color: 'rgba(0, 0, 0, 0.87)', // Keep text dark even when disabled
                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)', // For Safari
                              }
                            }
                          }}


                          helperText="Email cannot be changed, Contact Support"
                        />
                        <TextField
                          value={profileData.phone}
                          onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                          label="Phone"
                          fullWidth
                          InputProps={{
                            startAdornment: <Phone className="w-4 h-4 mr-2" />
                          }}
                        />
                        <TextField
                          value={profileData.location}
                          onChange={(e) => setProfileData({ ...profileData, location: e.target.value })}
                          label="Location"
                          fullWidth
                          InputProps={{
                            startAdornment: <MapPin className="w-4 h-4 mr-2" />
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <Box className="flex items-center">
                          <Mail className="w-5 h-5 mr-3" />
                          <Typography>{profileData.email}</Typography>
                        </Box>
                        <Box className="flex items-center">
                          <Phone className="w-5 h-5 mr-3" />
                          <Typography>{profileData.phone}</Typography>
                        </Box>
                        <Box className="flex items-center">
                          <MapPin className="w-5 h-5 mr-3" />
                          <Typography>{profileData.location}</Typography>
                        </Box>
                      </>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Professional Links */}
            <Grid item xs={12} md={6}>
              <Card {...cardProps}>
                <CardContent>
                  <Box className="flex justify-between items-center mb-4">
                    <Typography variant="h6" className="font-semibold">
                      Professional Links
                    </Typography>
                    <Tooltip title="Your professional online presence">
                      <IconButton size="small">
                        <HelpCircle className="w-4 h-4" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Stack spacing={3}>
                    {editMode ? (
                      <>
                        <TextField
                          value={profileData.linkedinUrl}
                          onChange={(e) => setProfileData({ ...profileData, linkedinUrl: e.target.value })}
                          label="LinkedIn URL"
                          fullWidth
                          InputProps={{
                            startAdornment: <Linkedin className="w-4 h-4 mr-2" />
                          }}
                        />
                        <TextField
                          value={profileData.companyUrl}
                          onChange={(e) => setProfileData({ ...profileData, companyUrl: e.target.value })}
                          label="Company Website"
                          fullWidth
                          InputProps={{
                            startAdornment: <Globe className="w-4 h-4 mr-2" />
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <Box className="flex items-center">
                          <Linkedin className="w-5 h-5 mr-3" />
                          <Typography>{profileData.linkedinUrl}</Typography>
                        </Box>
                        <Box className="flex items-center">
                          <Globe className="w-5 h-5 mr-3" />
                          <Typography>{profileData.companyUrl}</Typography>
                        </Box>
                      </>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Company Information */}
            <Grid item xs={12} md={6}>
              <Card {...cardProps}>
                <CardContent>
                  <Box className="flex justify-between items-center mb-4">
                    <Typography variant="h6" className="font-semibold">
                      Company Information
                    </Typography>
                    <Tooltip title="Your company details">
                      <IconButton size="small">
                        <HelpCircle className="w-4 h-4" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Stack spacing={3}>
                    {editMode ? (
                      <>
                        <TextField
                          value={profileData.company}
                          onChange={(e) => setProfileData({ ...profileData, company: e.target.value })}
                          label="Company Name"
                          fullWidth
                          InputProps={{
                            startAdornment: <Building className="w-4 h-4 mr-2" />
                          }}
                        />
                        <TextField
                          value={profileData.companyAddress}
                          onChange={(e) => setProfileData({ ...profileData, companyAddress: e.target.value })}
                          label="Company Address"
                          fullWidth
                          multiline
                          rows={2}
                          InputProps={{
                            startAdornment: <MapPin className="w-4 h-4 mr-2" />
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <Box className="flex items-center">
                          <Building className="w-5 h-5 mr-3" />
                          <Typography>{profileData.company}</Typography>
                        </Box>
                        <Box className="flex items-center">
                          <MapPin className="w-5 h-5 mr-3" />
                          <Typography>{profileData.companyAddress}</Typography>
                        </Box>
                      </>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Skills & Languages - MODIFIED WITH BIO */}
            <Grid item xs={12} md={6}>
              <Card {...cardProps}>
                <CardContent>
                  {/* Bio Section - NEW */}
                  <Box className="mb-4">
                    <Typography variant="h6" className="font-semibold">
                      Bio, Skills & Languages
                    </Typography>
                    <Typography variant="subtitle2" className="mb-2">
                      Professional Bio
                    </Typography>
                    {editMode ? (
                      <TextField
                        value={profileData.bio}
                        onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                        placeholder="Share your professional background, expertise, and what you're passionate about..."
                        fullWidth
                        multiline
                        rows={1}
                        sx={{ mb: 3 }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ mb: 3, lineHeight: 1.6 }}>
                        {profileData.bio || "No bio information provided."}
                      </Typography>
                    )}
                    <Divider sx={{ my: 2 }} />
                  </Box>
                  <Box className="flex justify-between items-center mb-4">

                    {editMode && (
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setSkillDialogOpen(true)}
                        >
                          Add Skill
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setLanguageDialogOpen(true)}
                        >
                          Add Language
                        </Button>
                      </Stack>
                    )}
                  </Box>



                  {/* Skills Section - EXISTING */}
                  <Box className="mb-4">
                    <Typography variant="subtitle2" className="mb-2">
                      Skills
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {profileData.skills.map((skill, index) => (
                        <Chip
                          key={index}
                          label={skill}
                          onDelete={editMode ? () => {
                            const newSkills = profileData.skills.filter((_, i) => i !== index);
                            setProfileData({ ...profileData, skills: newSkills });
                          } : undefined}
                          className="m-1"
                        />
                      ))}
                    </Stack>
                  </Box>

                  {/* Languages Section - EXISTING */}
                  <Box>
                    <Typography variant="subtitle2" className="mb-2">
                      Languages
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {profileData.languages.map((language, index) => (
                        <Chip
                          key={index}
                          label={language}
                          variant="outlined"
                          onDelete={editMode ? () => {
                            const newLanguages = profileData.languages.filter((_, i) => i !== index);
                            setProfileData({ ...profileData, languages: newLanguages });
                          } : undefined}
                          className="m-1"
                        />
                      ))}
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* QR Code Tab */}
        {isFeatureEnabled('EnhancedQRCodeGenerator') && (
          <TabPanel value={activeTab} index={1}>
            <EnhancedQRCodeGenerator
              profileData={profileData}
              getProfileSlug={getProfileSlug}
              isTrial={isTrial}
              navigate={navigate}
            />
          </TabPanel>
        )}
        {/* Email Signature Tab */}
        {isFeatureEnabled('EmailSignature') && (
          <TabPanel value={activeTab} index={3}>
            <Box sx={{ mb: 4 }}>
              {/* Template Gallery Button */}
              {companySettings.enableSignatureTemplates && (
                <Button
                  variant="outlined"
                  startIcon={<Layout />}
                  onClick={() => setTemplateGalleryOpen(true)}
                  sx={{ mb: 3 }}
                  disabled={isTrial}
                >
                  {isTrial ? "Upgrade to Access Template Gallery" : "Browse Signature Templates"}
                </Button>
              )}

              {/* Email Signature Generator Component with company settings */}
              <EmailSignatureGenerator
                profileData={profileData}
                companySettings={{
                  // Default to disabled (false) until backend is set up
                  enableAiSignature: false,
                  enableSignatureTemplates: false
                }}
                // companySettings={companySettings}
                getProfileSlug={getProfileSlug}
                isTrial={isTrial}
                navigate={navigate}
                setSnackbar={setSnackbar}
              />

              {/* Divider between sections */}
              <Divider sx={{ my: 5 }} />

              {/* AI Signature Content Generator - only render if enabled at company level */}
              {companySettings.enableAiSignature && (
                <AiSignatureContent
                  profileData={profileData}
                  isTrial={isTrial}
                  setSnackbar={setSnackbar}
                  onUpdateSignatureContent={(tagline, followUp, seasonal) => {
                    // Here you would update your email signature with the AI content
                    //console.log("Signature content updated:", { tagline, followUp, seasonal });
                    // This is where you would modify the signature state in the parent component
                  }}
                />
              )}
            </Box>

            {/* Signature Template Gallery Dialog */}
            {companySettings.enableSignatureTemplates && (
              <SignatureTemplateGallery
                open={templateGalleryOpen}
                onClose={() => setTemplateGalleryOpen(false)}
                onSelectTemplate={(template) => {
                  // Apply the selected template to the signature
                  setSignatureTheme(template.theme);
                  setSignatureColor(template.color);
                  setSignatureLayout(template.layout);
                  setIncludePhoto(template.includePhoto);
                  setIncludeQR(template.includeQR);

                  setSnackbar({
                    open: true,
                    message: `Template "${template.name}" applied successfully!`,
                    severity: 'success'
                  });
                }}
                profileData={profileData}
                isTrial={isTrial}
              />
            )}
          </TabPanel>
        )}
        {/* HTML Preview Tab */}
        {isFeatureEnabled('HTMLPreview') && (
          <TabPanel value={activeTab} index={4}>
            <HtmlPreviewTab
              profileData={profileData}
              generateProfilePageContent={generateProfilePageContent}
              isTrial={isTrial}
              exportFilename={exportFilename}
              setExportFilename={setExportFilename}
              getProfileSlug={getProfileSlug}
            />
          </TabPanel>
        )}
        {isFeatureEnabled('ProfilePageIDCard') && (
          <TabPanel value={activeTab} index={5}>
            <ProfilePageIDCard profileData={profileData} />
          </TabPanel>
        )}
        {/* Settings Tab */}
        <TabPanel value={activeTab} index={6}>
          <Grid container spacing={3}>
            
            <Grid item xs={12}>
              <Card {...cardProps}>
                <CardContent>
                  <Typography variant="h6" className="font-semibold mb-4">
                    Profile URL Settings
                  </Typography>
                       {/* Storage controls section */}
        {!adminMode && (
          <Box sx={{ mb: 4, p: 2, bgcolor: 'rgba(37, 99, 235, 0.05)', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Settings size={16} />
              URL Settings Storage
            </Typography>
            
            {/* <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={useUrlSettingsStorage}
                    onChange={handleToggleUrlSettingsStorage}
                  />
                }
                label="Remember my URL settings"
              />
            </Box>
             */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {isIndexedDBAvailable() 
                  ? 'Using Local storage' 
                  : 'Using cookie storage (limited)'}
              </Typography>
              
              <Stack direction="row" spacing={1}>
                {/* <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<Save size={14} />}
                  onClick={handleSaveUrlSettings}
                >
                  Save
                </Button> */}
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<RefreshCcw size={14} />}
                  onClick={handleResetUrlSettings}
                >
                  Reset
                </Button>
              </Stack>
            </Box>
          </Box>
        )}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      Configure how your profile QR codes and links are generated.
                    </Typography>
                  </Box>
                  <Stack spacing={3}>
                    <TextField
                      label="Company Profile Base URL"
                      value={profileData.profileBaseUrl}
                      onChange={(e) => setProfileData({
                        ...profileData,
                        profileBaseUrl: e.target.value
                      })}
                      fullWidth
                      placeholder="company.com"
                      helperText="Domain where employee profiles are hosted (without https:// or trailing slashes)"
                      InputProps={{
                        startAdornment: <Globe className="w-4 h-4 mr-2" />,
                      }}
                    />

                    <FormControl component="fieldset">
                      <FormLabel component="legend">Profile URL Format</FormLabel>
                      <RadioGroup
                        row
                        value={profileData.profileUrlType}
                        onChange={(e) => setProfileData({
                          ...profileData,
                          profileUrlType: e.target.value
                        })}
                      >
                        <FormControlLabel
                          value="name"
                          control={<Radio />}
                          label="Based on Name"
                        />
                        <FormControlLabel
                          value="id"
                          control={<Radio />}
                          label="Based on ID"
                        />

                        <FormControlLabel
                          value="custom"
                          control={<Radio />}
                          label="Custom Path"
                        />
                      </RadioGroup>
                    </FormControl>

                    {profileData.profileUrlType === "custom" && (
                      <TextField
                        label="Custom Profile Path"
                        value={profileData.customProfileSlug}
                        onChange={(e) => setProfileData({
                          ...profileData,
                          customProfileSlug: e.target.value
                        })}
                        fullWidth
                        helperText="Enter a custom URL path for your profile"
                      />
                    )}

                    {/* Export Filename Field */}
                    <TextField
                      label="Export Filename"
                      value={exportFilename}
                      onChange={(e) => setExportFilename(e.target.value)}
                      fullWidth
                      helperText="Filename used when exporting your profile (without extension)"
                      sx={{ mt: 2 }}
                    />

                    <Box sx={{
                      p: 2,
                      bgcolor: 'rgba(37, 99, 235, 0.1)',
                      borderRadius: 2,
                      border: '1px solid rgba(37, 99, 235, 0.2)'
                    }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Your Profile URL Preview:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          wordBreak: 'break-all'
                        }}
                      >
                        https://{profileData.profileBaseUrl}/profile/
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                          {getProfileSlug()}
                        </Box>
                      </Typography>
                    </Box>

                    <Button
                      startIcon={<Copy size={16} />}
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(`https://${profileData.profileBaseUrl}/profile/${getProfileSlug()}`);
                        setSnackbar({
                          open: true,
                          message: 'Profile URL copied to clipboard!',
                          severity: 'success'
                        });
                      }}
                    >
                      Copy URL
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>


            {/* <Grid item xs={12} md={6}>
              <Card {...cardProps}>
                <CardContent>
                  <Typography variant="h6" className="font-semibold mb-4">
                    Profile Settings
                  </Typography>
                  <Stack spacing={3}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={profileData.availability}
                          onChange={(e) => setProfileData({
                            ...profileData,
                            availability: e.target.checked
                          })}
                          color="primary"
                        />
                      }
                      label="Show Availability Status"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={profileData.profileVisibility === 'public'}
                          onChange={(e) => setProfileData({
                            ...profileData,
                            profileVisibility: e.target.checked ? 'public' : 'private'
                          })}
                          color="primary"
                        />
                      }
                      label="Public Profile"
                    />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card {...cardProps}>
                <CardContent>
                  <Typography variant="h6" className="font-semibold mb-4">
                    Notification Preferences
                  </Typography>
                  <Stack spacing={3}>
                    <FormControlLabel
                      control={<Switch defaultChecked color="primary" />}
                      label="Email Notifications"
                    />
                    <FormControlLabel
                      control={<Switch defaultChecked color="primary" />}
                      label="Profile View Alerts"
                    />
                    <FormControlLabel
                      control={<Switch color="primary" />}
                      label="QR Code Scan Notifications"
                    />
                  </Stack>
                </CardContent>
              </Card>
            </Grid> */}

            {/* Data & Privacy */}
            <Grid item xs={12}>
              <Card {...cardProps}>
                <CardContent>
                  <Typography variant="h6" className="font-semibold mb-4">
                    Data & Privacy
                  </Typography>
                  <Stack spacing={3}>
                    <Button
                      variant="outlined"
                      color="primary"
                      fullWidth
                      disabled={isTrial}
                      onClick={isTrial ? () => navigate('/profiles/upgrade') : null}
                    >
                      {isTrial ? "Upgrade to Download My Data" : "Download My Data"}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      fullWidth
                      onClick={handleDeleteProfile}
                      disabled={adminMode} // Disable delete button in admin mode
                    >
                      {adminMode ? 'Delete Not Allowed in Admin Mode' : 'Delete Profile'}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
        {/* Digital Wallet QR */}
        {isFeatureEnabled('DigitalWalletQR') && (
          <TabPanel value={activeTab} index={2}>
            <ProfilePageDigitalWalletQR
              profileData={profileData}
              getProfileSlug={getProfileSlug}
              isTrial={isTrial}
              navigate={navigate}
            />
          </TabPanel>
        )}
      </Paper>

      {/* Add Skill Dialog */}
      <Dialog open={skillDialogOpen} onClose={() => setSkillDialogOpen(false)}>
        <DialogTitle>Add New Skill</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Skill"
            fullWidth
            variant="outlined"
            value={newSkill}
            onChange={(e) => setNewSkill(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkillDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddSkill} variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Language Dialog */}
      <Dialog open={languageDialogOpen} onClose={() => setLanguageDialogOpen(false)}>
        <DialogTitle>Add New Language</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Language"
            fullWidth
            variant="outlined"
            value={newLanguage}
            onChange={(e) => setNewLanguage(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLanguageDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddLanguage} variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Profile Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={handleExportDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Export Profile Page</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" paragraph sx={{ mb: 3 }}>
            Export your profile as a standalone webpage that you can host on any web server.
            Choose the format that works best for your hosting environment.
          </Typography>

          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend">Export Format</FormLabel>
            <RadioGroup
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value)}
              row
            >
              <FormControlLabel value="html" control={<Radio />} label="HTML (Static Page)" />
              <FormControlLabel value="php" control={<Radio />} label="PHP (With View Counter)" />
            </RadioGroup>
          </FormControl>

          <TextField
            label="Filename (without extension)"
            value={exportFilename}
            onChange={(e) => setExportFilename(e.target.value)}
            fullWidth
            helperText={`Will be saved as: ${exportFilename || getProfileSlug()}.${exportFormat}`}
            margin="normal"
          />

          <Box sx={{
            p: 2,
            mt: 2,
            bgcolor: 'rgba(37, 99, 235, 0.1)',
            borderRadius: 2,
            border: '1px solid rgba(37, 99, 235, 0.2)'
          }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Upload Instructions:
            </Typography>
            <Typography variant="body2">
              1. After downloading, upload this file to your web hosting server.<br />
              2. Place it in your public web directory (usually "public_html" or "www").<br />
              3. Access it via: {exportFormat === 'html' ?
                `https://${profileData.profileBaseUrl}/${exportFilename || getProfileSlug()}.html` :
                `https://${profileData.profileBaseUrl}/${exportFilename || getProfileSlug()}.php`}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleExportDialogClose}>Cancel</Button>
          <Button
            onClick={copyProfilePageContent}
            color="secondary"
          >
            Copy Content
          </Button>
          <Button
            onClick={downloadProfilePage}
            variant="contained"
            startIcon={<Download />}
          >
            Download Profile
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfilePage;